// OSM Purchases — parts supervisors source from the outside market.
// Customer pays CAP the quoted price; CAP owes the OSM vendor the ACTUAL
// price. Superadmin records that cost + advance paid, and every open vendor
// balance stays visible here until settled.

import React, { useCallback, useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiEdit2,
  FiPackage,
  FiRefreshCw,
  FiRotateCcw,
  FiSearch,
  FiSlash,
  FiTag,
  FiTrendingUp,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, CustomPopup, PageHeader } from '../components/common/index.js';
import { osmApi } from '../api/stockApi';

const formatPrice = (value) => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const BUCKETS = [
  { value: '', label: 'All' },
  { value: 'to_price', label: 'Needs price' },
  { value: 'due', label: 'Balance due' },
  { value: 'settled', label: 'Settled' },
];

const STATUS_LABEL = { confirmed: 'Won', completed: 'Completed' };

// Same delivery-ETA presets the supervisor picks from when quoting an OSM
// part, and the same ones used for exchange returns — admin can update this
// once the vendor confirms a real timeline; it feeds the customer-facing quote.
const OSM_ETA_OPTIONS = ['Ready Stock', '3 to 5 Days', '7 to 10 Days'];

const StatCard = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    sky: 'bg-sky-50 text-sky-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="truncate text-lg font-bold text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

const EditOsmModal = ({ row, submitting, error, onClose, onSave, onAddPayment, onDeletePayment, paymentBusy, onMarkUnavailable, onRestore, unavailableBusy }) => {
  const [actual, setActual] = useState(row.actual_price ? String(Number(row.actual_price)) : '');
  const [settled, setSettled] = useState(row.settled);
  // Only pre-select the ETA dropdown when the current note IS one of the
  // presets — a custom free-text note is left alone unless admin picks a preset.
  const initialEta = OSM_ETA_OPTIONS.includes(row.delivery_eta) ? row.delivery_eta : '';
  const [eta, setEta] = useState(initialEta);
  const customEtaNote = row.delivery_eta && !OSM_ETA_OPTIONS.includes(row.delivery_eta) ? row.delivery_eta : '';
  const [note, setNote] = useState(row.note || '');
  const [formError, setFormError] = useState('');

  // Advance ledger — each payment is added/removed live (not on Save), so the
  // vendor cost can be paid down over several days.
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState('');
  const [payError, setPayError] = useState('');

  // Couldn't-procure — refund the customer for a booked part that never arrived.
  const [unavailReason, setUnavailReason] = useState('');
  const canMarkUnavailable = !row.osm_unavailable && !row.dispatched && row.inquiry.status !== 'completed';
  const refundPaid = row.customer_refund?.paid;

  const actualNum = Number(actual);
  const payments = row.payments ?? [];
  const advanceTotal = Number(row.advance_amount ?? 0);   // server truth (sum of ledger)
  const balance = actual !== '' && Number.isFinite(actualNum)
    ? (settled ? 0 : actualNum - advanceTotal)
    : null;
  const sold = row.sold_price != null ? Number(row.sold_price) : null;
  const profit = sold != null && actual !== '' && Number.isFinite(actualNum) ? sold - actualNum : null;

  const submit = (e) => {
    e.preventDefault();
    setFormError('');
    if (actual !== '' && (!Number.isFinite(actualNum) || actualNum < 0)) {
      setFormError('Enter a valid actual price'); return;
    }
    if (actual !== '' && actualNum < advanceTotal) {
      setFormError(`Actual price can't be below advances already paid (${formatPrice(advanceTotal)})`); return;
    }
    if (settled && actual === '') {
      setFormError('Enter the actual price before marking settled'); return;
    }
    const payload = { actual_price: actual === '' ? null : actualNum, settled, note: note.trim() };
    // Only send the ETA if admin actually touched the dropdown — otherwise
    // leave an existing custom note (that isn't one of the presets) alone.
    if (eta !== initialEta) payload.supervisor_notes = eta;
    onSave(payload);
  };

  const addPayment = async () => {
    setPayError('');
    const amt = Number(payAmount);
    if (payAmount === '' || !Number.isFinite(amt) || amt <= 0) {
      setPayError('Enter a valid advance amount'); return;
    }
    const body = { amount: amt };
    if (payDate) body.paid_at = payDate;
    const ok = await onAddPayment(body);
    if (ok) { setPayAmount(''); setPayDate(''); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{row.name}{row.quantity > 1 ? ` ×${row.quantity}` : ''}</h3>
            <p className="mt-0.5 text-sm text-slate-500">{row.inquiry.car} · {row.inquiry.customer_name}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <FiX className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="mt-4 space-y-4">
          {error && <Alert type="error" message={error} />}
          {formError && <Alert type="error" message={formError} />}

          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-baseline justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sold to customer</p>
              <p className="text-lg font-bold text-slate-900">{formatPrice(row.sold_price)}</p>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Actual price (vendor) *</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                type="number" min="0" step="any" inputMode="numeric"
                value={actual} onChange={(e) => setActual(e.target.value)} disabled={submitting}
                placeholder="e.g. 80000"
                className="w-full rounded-xl border border-slate-200 py-2.5 pl-7 pr-3 text-sm font-semibold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
            {profit != null && (
              <p className="mt-1.5 text-xs text-slate-400">
                Profit vs sold:{' '}
                <span className={`font-bold ${profit < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{formatPrice(profit)}</span>
              </p>
            )}
          </div>

          {/* Advance ledger — pay the vendor down over a few days (each is an
              "advance"; save the actual price first so the balance is right). */}
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-700">Advances paid</p>
              <p className="text-sm font-bold text-slate-900">
                {formatPrice(advanceTotal)}
                {balance != null && (
                  <span className={balance > 0 ? 'text-rose-600' : 'text-emerald-600'}> · {formatPrice(balance)} left</span>
                )}
              </p>
            </div>

            {payments.length > 0 ? (
              <ul className="mb-2 space-y-1.5">
                {payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-bold text-slate-800">{formatPrice(p.amount)}</span>
                    <span className="flex-1 truncate text-right text-xs text-slate-400">
                      {p.paid_at_display}{p.note ? ` · ${p.note}` : ''}
                    </span>
                    <button type="button" onClick={() => onDeletePayment(p.id)} disabled={paymentBusy}
                      title="Remove advance"
                      className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50">
                      <FiX className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-2 text-xs text-slate-400">No advances yet — add one below.</p>
            )}

            {payError && <p className="mb-2 text-xs font-semibold text-rose-600">{payError}</p>}

            {!settled && (
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
                  <input
                    type="number" min="0" step="any" inputMode="numeric"
                    value={payAmount} onChange={(e) => setPayAmount(e.target.value)} disabled={paymentBusy}
                    placeholder="Advance amount"
                    className="w-full rounded-xl border border-slate-200 py-2 pl-7 pr-3 text-sm font-semibold focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                  />
                </div>
                <input
                  type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} disabled={paymentBusy}
                  className="rounded-xl border border-slate-200 px-2 py-2 text-sm text-slate-600 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
                <Button type="button" variant="secondary" onClick={addPayment} loading={paymentBusy} disabled={paymentBusy}>Add</Button>
              </div>
            )}
          </div>

          <div>
            <CustomPopup
              label="Delivery ETA"
              name="delivery_eta"
              value={eta}
              onChange={(e) => setEta(e.target.value)}
              disabled={submitting}
              clearable
              searchable={false}
              placeholder="Not set — pick a vendor delivery estimate"
              options={OSM_ETA_OPTIONS}
            />
            {customEtaNote && (
              <p className="mt-1.5 text-xs italic text-slate-400">
                Current note: “{customEtaNote}” — picking a preset above replaces it.
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-700">Note</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)} disabled={submitting}
              placeholder="e.g. engine from XYZ motors"
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3">
            <input type="checkbox" checked={settled} onChange={(e) => setSettled(e.target.checked)} disabled={submitting}
                   className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-400" />
            <span className="text-sm font-semibold text-slate-700">Vendor fully paid (settled)</span>
          </label>

          {/* Couldn't procure — refund the customer for this booked line */}
          {row.osm_unavailable ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
              <div className="flex items-center gap-2 text-sm font-bold text-rose-700">
                <FiSlash className="h-4 w-4" /> Couldn’t procure — customer refunded
              </div>
              {row.customer_refund ? (
                <p className="mt-1 text-xs text-rose-600">
                  Refund {formatPrice(row.customer_refund.amount)} to customer —{' '}
                  {refundPaid ? 'paid' : 'pending in Expenses'}.
                </p>
              ) : (
                <p className="mt-1 text-xs text-rose-600">No customer payment to refund (pre-payment).</p>
              )}
              {row.vendor_advance_to_recover && (
                <p className="mt-1 text-xs font-semibold text-amber-700">
                  ⚠ {formatPrice(row.vendor_advance_to_recover)} advance already paid to the vendor — recover it or write it off.
                </p>
              )}
              {row.osm_unavailable_reason && (
                <p className="mt-1 text-xs italic text-rose-500">“{row.osm_unavailable_reason}”</p>
              )}
              {!refundPaid && (
                <button
                  type="button" onClick={onRestore} disabled={unavailableBusy}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-xs font-bold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  <FiRotateCcw className="h-3.5 w-3.5" /> Undo — part is available after all
                </button>
              )}
            </div>
          ) : canMarkUnavailable ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
              <p className="text-sm font-bold text-amber-800">Couldn’t get this part?</p>
              <p className="mt-0.5 text-xs text-amber-700">
                Mark it not available to refund the customer for this line. It leaves the delivery &amp; dispatch flow.
              </p>
              <input
                type="text" value={unavailReason} onChange={(e) => setUnavailReason(e.target.value)} disabled={unavailableBusy}
                placeholder="Reason (optional) — e.g. vendor out of stock"
                className="mt-2 w-full rounded-xl border border-amber-200 px-3 py-2 text-sm focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200"
              />
              <button
                type="button" onClick={() => onMarkUnavailable(unavailReason.trim())} disabled={unavailableBusy}
                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                <FiSlash className="h-3.5 w-3.5" /> Mark not available &amp; refund customer
              </button>
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="primary" loading={submitting}>Save</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OsmPage = () => {
  const [bucket, setBucket] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [unavailableBusy, setUnavailableBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await osmApi.getList({ bucket, q });
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load OSM purchases');
    } finally {
      setLoading(false);
    }
  }, [bucket, q]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSubmitting(true);
    setModalError('');
    try {
      await osmApi.set(target.part_id, payload);
      setTarget(null);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Advance ledger — add/remove a payment live, then refresh the open modal
  // (with the returned part) and the list totals underneath.
  const addPayment = async (body) => {
    setPaymentBusy(true);
    setModalError('');
    try {
      const res = await osmApi.addPayment(target.part_id, body);
      setTarget((res.data ?? res).part);
      load();
      return true;
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not add advance');
      return false;
    } finally {
      setPaymentBusy(false);
    }
  };

  const deletePayment = async (paymentId) => {
    setPaymentBusy(true);
    setModalError('');
    try {
      const res = await osmApi.removePayment(target.part_id, paymentId);
      setTarget((res.data ?? res).part);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not remove advance');
    } finally {
      setPaymentBusy(false);
    }
  };

  // Couldn't procure — open the customer refund (or undo it).
  const markUnavailable = async (reason) => {
    setUnavailableBusy(true);
    setModalError('');
    try {
      const res = await osmApi.markUnavailable(target.part_id, { reason });
      setTarget((res.data ?? res).part);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not mark unavailable');
    } finally {
      setUnavailableBusy(false);
    }
  };

  const restoreAvailable = async () => {
    setUnavailableBusy(true);
    setModalError('');
    try {
      const res = await osmApi.restore(target.part_id);
      setTarget((res.data ?? res).part);
      load();
    } catch (e) {
      setModalError(e?.response?.data?.error || e.message || 'Could not undo');
    } finally {
      setUnavailableBusy(false);
    }
  };

  const rows = data?.parts ?? [];
  const bucketCounts = { '': rows.length, to_price: data?.to_price_count ?? 0, due: data?.due_count ?? 0, settled: data?.settled_count ?? 0 };

  return (
    <MainLayout>
      <PageHeader purpose="Outside-market parts — what the vendor charged vs what the customer paid, and what is still owed.">
        <Button variant="secondary" onClick={load} className="gap-2"><FiRefreshCw className="h-4 w-4" /> Refresh</Button>
      </PageHeader>

      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      {/* Summary */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={FiTag} label="Needs price" value={data?.to_price_count ?? '—'} tone="amber" />
        <StatCard icon={FiCreditCard} label="Balance to vendors" value={formatPrice(data?.total_balance_due)} tone="rose" />
        <StatCard icon={FiClock} label="Advance paid" value={formatPrice(data?.total_advance_paid)} tone="sky" />
        <StatCard icon={FiTrendingUp} label="Sold total" value={formatPrice(data?.total_sold)} tone="emerald" />
      </div>

      {/* Tabs + search */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {BUCKETS.map((b) => {
            const active = bucket === b.value;
            return (
              <button
                key={b.value || 'all'}
                type="button"
                onClick={() => setBucket(b.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
              >
                {b.label}
                {b.value !== '' && (
                  <span className={`rounded-full px-1.5 text-xs font-bold ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                    {bucketCounts[b.value]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Part, car or customer…"
            className="w-64 rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-14 text-center">
          <FiPackage className="mx-auto mb-2 h-8 w-8 text-slate-200" />
          <p className="text-sm text-slate-400">No OSM parts here yet — they appear when supervisors quote parts from the outside market</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <div key={row.part_id} className={`rounded-xl border bg-white p-4 shadow-sm ${row.osm_unavailable ? 'border-rose-300' : row.bucket === 'due' ? 'border-rose-200' : row.bucket === 'to_price' ? 'border-amber-200' : 'border-slate-200'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${row.bucket === 'settled' ? 'bg-emerald-50 text-emerald-600' : row.bucket === 'due' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'}`}>
                    <FiPackage className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-base font-bold text-slate-900">
                      {row.name}{row.quantity > 1 ? ` ×${row.quantity}` : ''}
                    </h3>
                    <p className="mt-0.5 text-sm text-slate-500">{row.inquiry.car} · {row.inquiry.customer_name}</p>
                    <p className="mt-0.5 text-xs font-medium text-slate-400">
                      Inquiry #{row.inquiry.id} · {STATUS_LABEL[row.inquiry.status] || row.inquiry.status} · by {row.inquiry.salesperson_name || '-'}
                    </p>
                    {row.delivery_eta && (
                      <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                        <FiClock className="h-3 w-3" /> {row.delivery_eta}
                      </span>
                    )}
                    {row.note && <p className="mt-1 text-xs italic text-slate-400">“{row.note}”</p>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {row.bucket === 'to_price' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
                      <FiAlertCircle className="h-3.5 w-3.5" /> Enter vendor price
                    </span>
                  ) : row.bucket === 'due' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-600">
                      <FiCreditCard className="h-3.5 w-3.5" /> {formatPrice(row.balance_due)} to vendor
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                      <FiCheckCircle className="h-3.5 w-3.5" /> Settled
                    </span>
                  )}
                  <Button size="sm" variant={row.bucket === 'settled' ? 'secondary' : 'primary'} onClick={() => setTarget(row)} className="gap-1.5">
                    <FiEdit2 className="h-3.5 w-3.5" /> {row.bucket === 'to_price' ? 'Add price' : 'Edit'}
                  </Button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Sold to customer</p>
                  <p className="mt-0.5 text-sm font-bold text-slate-800">{formatPrice(row.sold_price)}</p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Vendor price</p>
                  <p className="mt-0.5 text-sm font-bold text-slate-800">{formatPrice(row.actual_price)}</p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Advance / Balance{row.advance_count > 0 ? ` · ${row.advance_count}×` : ''}
                  </p>
                  <p className="mt-0.5 text-sm font-bold text-slate-800">
                    {formatPrice(row.advance_amount)} <span className={Number(row.balance_due) > 0 ? 'text-rose-600' : 'text-emerald-600'}>/ {formatPrice(row.balance_due)}</span>
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Profit</p>
                  <p className={`mt-0.5 text-sm font-bold ${row.profit != null && Number(row.profit) < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{formatPrice(row.profit)}</p>
                </div>
              </div>

              {row.osm_unavailable && (
                <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs">
                  <span className="inline-flex items-center gap-1 font-bold text-rose-700">
                    <FiSlash className="h-3.5 w-3.5" /> Couldn’t procure — customer refunded
                  </span>
                  {row.customer_refund && (
                    <span className="text-rose-600">
                      {formatPrice(row.customer_refund.amount)} {row.customer_refund.paid ? '(paid)' : '(pending)'}
                    </span>
                  )}
                  {row.vendor_advance_to_recover && (
                    <span className="font-semibold text-amber-700">
                      · {formatPrice(row.vendor_advance_to_recover)} vendor advance to recover
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {target && (
        <EditOsmModal
          row={target}
          submitting={submitting}
          error={modalError}
          onClose={() => { setTarget(null); setModalError(''); }}
          onSave={save}
          onAddPayment={addPayment}
          onDeletePayment={deletePayment}
          paymentBusy={paymentBusy}
          onMarkUnavailable={markUnavailable}
          onRestore={restoreAvailable}
          unavailableBusy={unavailableBusy}
        />
      )}
    </MainLayout>
  );
};

export default OsmPage;
